'use client';

/**
 * Unified inbox — master–detail root (#3613 follow-up).
 *
 * The inquiry list (the "master") is hosted by the route layout (see
 * inbox/layout.tsx) as a persistent column on desktop. This root page is the
 * detail column at the list root:
 *  - Desktop: a "nothing selected" placeholder (the layout shows the list).
 *  - Mobile: the list itself, full-width (the layout's master column is
 *    hidden < mobile-view), so the list is reachable without a sidebar panel.
 * Selecting a row navigates to `/inbox/[matchId]`, which renders the detail.
 */

import { InboxListPanel } from '@/components/inquiries/InboxListPanel';
import { InboxTour } from '@/elements/tour/useInboxTour';
import { Inbox as InboxIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';

export default function InboxPage() {
  const t = useTranslations('Lawyers.inbox');

  return (
    <>
      {/* Mobile: the list is the root view. Offset below the fixed mobile
          header (FloatingHeaderControls, top:0 / z-1000) so the list/empty
          state isn't hidden behind it — same header-offset convention the search
          and research pages use. Reset on large screens where the header is
          floating controls instead of a full-width bar. */}
      <div className='mobile-view:hidden h-full w-full overflow-hidden bg-surface-sidebar pt-16 compact-header:pt-0'>
        <InboxListPanel />
      </div>
      {/* Desktop: the layout already shows the list; this is the detail placeholder. */}
      <div className='hidden mobile-view:flex h-full w-full items-center justify-center p-6'>
        <div className='flex max-w-sm flex-col items-center text-center'>
          <InboxIcon
            className='mb-4 h-12 w-12 text-content-muted'
            aria-hidden='true'
          />
          <h1 className='mb-2 text-lg font-semibold text-content-primary'>
            {t('selectPromptTitle')}
          </h1>
          <p className='text-sm text-content-secondary'>
            {t('selectPromptDescription')}
          </p>
        </div>
      </div>
      {/* Mounted on the list root only, not the `[matchId]` detail route.
          Self-gating on desktop + the professional role tabs. */}
      <InboxTour />
    </>
  );
}
